"use client"

import { useState, useEffect, useMemo } from "react"
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Button,
  Divider,
  Chip,
  Grid,
  useTheme,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"

const ModalAsociarItemPorCliente = ({ open, handleClose, entidades, onAssociate }) => {
  const theme = useTheme()
  const [searchSource, setSearchSource] = useState("")
  const [searchTarget, setSearchTarget] = useState("")
  const [sourceId, setSourceId] = useState(null)
  const [targetId, setTargetId] = useState(null)

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setSearchSource("")
      setSearchTarget("")
      setSourceId(null)
      setTargetId(null)
    }
  }, [open])

  // Filter opciones de origen (excluye entidad destino)
  const filteredSource = useMemo(
    () =>
      entidades.filter(
        (e) =>
          e.id !== targetId &&
          (e.descripcion.toLowerCase().includes(searchSource.toLowerCase()) ||
            (e.apellido && e.apellido.toLowerCase().includes(searchSource.toLowerCase())))
      ),
    [entidades, targetId, searchSource]
  )

  // Filter opciones de destino (excluye entidad origen)
  const filteredTarget = useMemo(
    () =>
      entidades.filter(
        (e) =>
          e.id !== sourceId &&
          (e.descripcion.toLowerCase().includes(searchTarget.toLowerCase()) ||
            (e.apellido && e.apellido.toLowerCase().includes(searchTarget.toLowerCase())))
      ),
    [entidades, sourceId, searchTarget]
  )

  const handleAssociateClick = () => {
    if (!sourceId || !targetId) {
      alert("Debe seleccionar ambas entidades antes de continuar.")
      return
    }
    onAssociate(sourceId, targetId)
    handleClose()
  }

  const sourceEntity = entidades.find((e) => e.id === sourceId)
  const targetEntity = entidades.find((e) => e.id === targetId)

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-asociar-precios">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 600, md: 800 },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6">Asociar Precios entre Clientes</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        {/* Body */}
        <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Entidad a copiar precios
              </Typography>
              <Autocomplete
                options={filteredSource}
                getOptionLabel={(e) =>
                  e.descripcion + (e.apellido ? ` ${e.apellido}` : "")
                }
                value={sourceEntity || null}
                onChange={(_, val) => setSourceId(val ? val.id : null)}
                inputValue={searchSource}
                onInputChange={(_, val) => setSearchSource(val)}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar entidad origen" size="small" />
                )}
                fullWidth
              />
              {sourceEntity && (
                <Chip
                  label={`Copiar de: ${sourceEntity.descripcion}${
                    sourceEntity.apellido ? ` ${sourceEntity.apellido}` : ""
                  }`}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Entidad replicadora
              </Typography>
              <Autocomplete
                options={filteredTarget}
                getOptionLabel={(e) =>
                  e.descripcion + (e.apellido ? ` ${e.apellido}` : "")
                }
                value={targetEntity || null}
                onChange={(_, val) => setTargetId(val ? val.id : null)}
                inputValue={searchTarget}
                onInputChange={(_, val) => setSearchTarget(val)}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar entidad destino" size="small" />
                )}
                fullWidth
              />
              {targetEntity && (
                <Chip
                  label={`Copiar a: ${targetEntity.descripcion}${
                    targetEntity.apellido ? ` ${targetEntity.apellido}` : ""
                  }`}
                  color="secondary"
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        <Divider />
        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!sourceId || !targetId}
            onClick={handleAssociateClick}
          >
            Asociar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default ModalAsociarItemPorCliente
